import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextInput,
  TextAreaInput,
  SelectBox,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import {
  cocTypeOptions,
  CocModalProps,
  CocSignatureObject,
} from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

interface EditCocModalProps extends CocModalProps {}

interface CocFormData {
  custody_from: string;
  custody_to: string;
  reason: string;
  timestamp: string;
  type: string;
}

const cocValidationSchema = z.object({
  custody_from: z
    .string({
      required_error: "Custody from is required",
    })
    .min(1, { message: "Custody from is required" }),
  custody_to: z
    .string({
      required_error: "Custody to is required",
    })
    .min(1, { message: "Custody to is required" }),
  reason: z
    .string({
      required_error: "Notes are required",
    })
    .min(1, { message: "Notes are required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
  type: z
    .string({
      required_error: "COC type is required",
    })
    .min(1, { message: "COC type is required" }),
});

const EditCocModal: React.FC<EditCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CocFormData>({
    mode: "uncontrolled",
    initialValues: {
      custody_from: defaultInfo.custody_from,
      custody_to: defaultInfo.custody_to,
      reason: defaultInfo.reason,
      timestamp: defaultInfo.timestamp,
      type: defaultInfo.type,
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const custodyToSig = useRef<SignatureFieldRef>();
  const custodyFromSig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(cocValidationSchema)(formData);
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_to_sig: createSignature(
          custodyToSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
        custody_from_sig: createSignature(
          custodyFromSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const updateData = {
        ...formData,
        signature: JSON.stringify(signature),
        timestamp: db_timestamp(formData.timestamp, true),
        evidence_id: defaultInfo.evidence_id,
      };

      await EvidenceCocAPI.updateCocRecord(defaultInfo.coc_id, updateData);

      enqueueSnackbar(`Chain of Custody Record Updated`, {
        variant: "success",
      });

      onSubmit?.({
        ...updateData,
        coc_id: defaultInfo.coc_id,
        evidence_id: defaultInfo.evidence_id,
      });
    } catch (error) {
      console.error("Error updating CoC record:", error);
      enqueueSnackbar(`Error updating Chain of Custody record.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Edit Chain of Custody Record</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Custody From"
            required
            placeholder="Enter..."
            description="Name of person with current custody"
            key={form.key("custody_from")}
            {...form.getInputProps("custody_from")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="Custody To"
            required
            placeholder="Enter..."
            description="Name of person receiving item"
            key={form.key("custody_to")}
            {...form.getInputProps("custody_to")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          {defaultInfo.type !== "Intake" ? (
            <SelectBox
              label="COC Type"
              required
              data={cocTypeOptions}
              key={form.key("type")}
              {...form.getInputProps("type")}
            />
          ) : (
            <div></div>
          )}
          <TextAreaInput
            label="Notes"
            required
            colSpan={2}
            minRows={4}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={custodyFromSig}
              defaultValue={
                defaultInfo?.signature?.custody_from_sig?.signature || undefined
              }
              label="Custody From Signature"
            />
          </div>
          <div>
            <SignatureField
              sigFieldRef={custodyToSig}
              defaultValue={
                defaultInfo?.signature?.custody_to_sig?.signature || undefined
              }
              label="Custody To Signature"
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Edit Record
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default EditCocModal;
